import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import FullScreenView from "../fullscreen-view";
import { Col, Row } from "antd";
import moment from "moment";
import classnames from "classnames";
import FCFormControl from "Components/fullscreen-configuration/form-control";
import classNames from "classnames";
import { useHistory } from "react-router-dom";
import {
  fetchRacks,
  fetchServicePoint,
  listServicePoints,
} from "Services/delivery-settings.services";
import useSession from "Hooks/session.hook";
import { FullscreenConfigEnums, SortingLinkTypePrefix } from "Data/settings";
import {
  createTrackingConfig,
  deleteTrackingConfig,
  fetchTrackingConfigList,
  updateTrackingConfig,
} from "Services/fullscreen.services";
import { omitBy, isNil } from "lodash";

type optionType = {
  racks: any[];
  locations: any[];
};

const initialOptions = {
  racks: [],
  locations: [],
};

const useConfigurationView = () => {
  const [parentInput, setParentInput] = useState<any>({});
  const [urlList, setUrlList] = useState<any>([]);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<optionType>(initialOptions);
  const [hasSidePanelMessage, setSidePanelMessage] = useState<string | null>();
  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);

  const { t } = useTranslation();
  const { org } = useSession();

  const handleFetchTrackingConfigList = async (
    type?: FullscreenConfigEnums,
  ) => {
    const payload: any = omitBy(
      {
        companyOrgID: org?._id,
        type: type,
      },
      isNil,
    );

    // for now, fetch every list, since pagination is not integrated yet
    const responseData: any = await fetchTrackingConfigList(payload);

    if (!responseData.error) {
      // map by rack id and rack name only
      setUrlList(responseData);
    }
  };

  const handleModifyTrackingConfig = async (data) => {
    const payload = {
      ...data,
      companyOrgID: org?._id,
    };

    setIsSubmitLoading(true);

    // for now, fetch every list, since pagination is not integrated yet
    const responseData: any = await (payload.isNew
      ? createTrackingConfig(payload)
      : updateTrackingConfig(payload));

    if (!responseData.error) {
      const msg = payload.isNew
        ? t("txt.successfullyCreated", { field: t("label.configuration") })
        : t("txt.successfullyUpdated", { field: t("label.configuration") });

      setTimeout(() => {
        setIsSubmitLoading(false);
        setSidePanelMessage(msg);
        payload.isNew
          ? setIsSidePanelOpen(false)
          : setParentInput((prev) => ({ ...prev, ...payload, isEdit: false }));
        handleFetchTrackingConfigList();
      }, 3000);
    } else {
      setIsSubmitLoading(false);

      const msg = payload.isNew
        ? t("txt.failedToCreate", { field: t("label.configuration") })
        : t("txt.failedToUpdate", { field: t("label.configuration") });
      setSidePanelMessage(msg);
    }
  };

  const handleDeleteTrackingConfig = async () => {
    let id: SortingLinkTypePrefix | null = parentInput._id;

    if (!id) return;

    setIsSubmitLoading(true);

    // for now, fetch every list, since pagination is not integrated yet
    const responseData: any = await deleteTrackingConfig(id);

    if (!responseData.error) {
      const msg = t("txt.successfullyDeleted", {
        field: t("label.configuration"),
      });

      setTimeout(() => {
        setIsSubmitLoading(false);
        setIsSidePanelOpen(false);
        setParentInput({});
        handleFetchTrackingConfigList();
      }, 3000);
    } else {
      setIsSubmitLoading(false);

      const msg = t("txt.failedToDelete", { field: t("label.configuration") });
    }
  };

  const handleFetchRackList = async () => {
    const responseData: any = await fetchRacks({active: true});

    if (!responseData.error) {
      // map by rack id and rack name only
      const racks = responseData.map((r) => ({
        _id: r?.rack?._id,
        name: r?.rack?.name,
      }));

      setOptions((prev) => ({ ...prev, racks: racks }));
    }
  };

  const handleFetchServicePointList = async () => {
    const responseData: any = await listServicePoints({active: true});

    if (!responseData.error) {
      // map by rack id and rack name only
      const locations = responseData.map((r) => ({
        _id: r?._id,
        name: r?.locationName,
      }));

      setOptions((prev) => ({
        ...prev,
        locations: [{ name: "All", _id: "" }].concat(locations),
      }));
    }
  };

  const handleInsertIntoParent = (id: string) => {
    const data = urlList.find((u) => u._id === id);

    if (data) setParentInput(data);
  };

  const handleAddNew = () => {
    if (parentInput.isEdit) {
      if (confirm(t("txt.yourChangesWillNotBeSaved"))) {
        setParentInput({});
      }

      return;
    }

    setParentInput({ isNew: true });
    !isSidePanelOpen && setIsSidePanelOpen(true);
  };

  const handleEditParentInput = (id: string) => {
    // check if the id is the same, do nothing
    if (id === parentInput._id) return;

    // if the sidepanel is open, something is being edited
    // warn the user
    if (parentInput.isEdit) {
      if (confirm(t("txt.yourChangesWillNotBeSaved"))) {
        handleInsertIntoParent(id);
      }

      return;
    }

    handleInsertIntoParent(id);
    !isSidePanelOpen && setIsSidePanelOpen(true);
  };

  return {
    parentInput,
    setParentInput,
    urlList,
    setUrlList,
    isSidePanelOpen,
    setIsSidePanelOpen,
    options,
    setOptions,
    hasSidePanelMessage,
    setSidePanelMessage,
    isSubmitLoading,
    setIsSubmitLoading,
    handleFetchRackList,
    handleFetchServicePointList,
    handleInsertIntoParent,
    handleAddNew,
    handleEditParentInput,
    handleFetchTrackingConfigList,
    handleModifyTrackingConfig,
    handleDeleteTrackingConfig,
  };
};

const ConfigurationView: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const {
    parentInput,
    setParentInput,
    urlList,
    setUrlList,
    isSidePanelOpen,
    setIsSidePanelOpen,
    options,
    setOptions,
    hasSidePanelMessage,
    setSidePanelMessage,
    isSubmitLoading,
    setIsSubmitLoading,
    handleFetchRackList,
    handleFetchServicePointList,
    handleInsertIntoParent,
    handleAddNew,
    handleEditParentInput,
    handleFetchTrackingConfigList,
    handleModifyTrackingConfig,
    handleDeleteTrackingConfig,
  } = useConfigurationView();

  useEffect(() => {
    // fetch racks
    handleFetchRackList();
    handleFetchServicePointList();
    handleFetchTrackingConfigList();

    return () => {
      setOptions(initialOptions);
      setUrlList([]);
    };
  }, []);

  const formProps = {
    urlList,
    isSidePanelOpen,
    setIsSidePanelOpen,
    parentInput,
    setParentInput,
    options,
    onSubmit: handleModifyTrackingConfig,
    onDelete: handleDeleteTrackingConfig,
    hasSidePanelMessage,
    setSidePanelMessage,
    isSubmitLoading,
    setIsSubmitLoading,
  };

  return (
    <FullScreenView>
      <div className="fcv-container">
        <div className={classnames("fcv-inner", { shrink: isSidePanelOpen })}>
          <div className="fcv-header">
            <div className="fcv-h-left">
              <div className="fcv-h-title">{t("title.configuration")}</div>
            </div>
            <div className="fcv-h-right">
              <div className="fcv-h-btn-container">
                <span
                  className="fcv-custom-btn"
                  onClick={() =>
                    history.push(
                      "/delivery-management/parcel-tracking?tabKey=2&subTab=4",
                    )
                  }
                >
                  {t("action.goToDashboard")}
                </span>
                <span
                  className="fcv-custom-btn"
                  onClick={() => history.push("/fullscreen/tracking")}
                >
                  {t("action.viewFullscreen")}
                </span>
                <span
                  className="fcv-custom-btn primary"
                  onClick={() => handleAddNew()}
                >
                  {t("action.addNew")}
                </span>
              </div>
            </div>
          </div>
          <div className="fcv-content">
            <Row gutter={[16, 16]}>
              {urlList.map((m) => (
                <Col span={24} lg={6} key={m._id}>
                  <div
                    className={classNames("cvc-card-container", {
                      active: parentInput?._id === m._id,
                    })}
                    onClick={() => handleEditParentInput(m._id)}
                  >
                    <div className="cvc-cc-top">
                      <div className="cvc-ct-title">{m.name}</div>
                      <div className="cvc-ct-subtitle">{m.shortUrl}</div>
                    </div>
                    <div className="cvc-cc-bottom">
                      <div className="cvc-cb-date">
                        {moment(m.createdAt).format("DD/MM/YYYY")}
                      </div>
                      <div className={`cvc-cb-type ${m.mapType}`}>
                        {m.mapType}
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </div>
        <FCFormControl {...formProps} />
      </div>
    </FullScreenView>
  );
};

export default ConfigurationView;
